import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth, canSee, seesMoney } from "@/context/AuthContext";
import { MaterialsCoverage } from "@/components/MaterialsCoverage";
import { PaymentMethods } from "@/components/PaymentMethods";
import { MyNotes } from "@/components/MyNotes";
import { eur, num, dateParams, currentMonthRange, previousRange } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import DateRangeFilter from "@/components/DateRangeFilter";
import { differenceInCalendarDays, subMonths, addDays, format as fmt } from "date-fns";
import { ReturnsModeSelect, useReturnsMode } from "@/components/ReturnsModeSelect";
import { Delta } from "@/components/Delta";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingBag, Package, TrendingUp, Factory, AlertTriangle, Euro, Loader2,
  ChevronDown, ChevronRight, Undo2, UserPlus,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, Line, Legend, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(y, mo - 1).toLocaleDateString("it-IT", { month: "short" });
};

const KpiLink = ({ to, children }) => (to ? <Link to={to} className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary [&>div]:cursor-pointer" data-testid={`kpi-link-${to.slice(1)}`}>{children}</Link> : children);

function Kpi({ icon: Icon, label, value, sub, accent, cur, prev, to }) {
  return (
    <KpiLink to={to}>
    <Card className="h-full p-5 transition-colors hover:border-primary/40" data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-2">
            <p className="font-display text-2xl font-bold tracking-tight">{value}</p>
            <Delta current={cur} previous={prev} />
          </div>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${accent}`}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
      </div>
    </Card>
    </KpiLink>
  );
}

const dayLabel = (d) => fmt(d, "d/M");

const ChannelLine = ({ label, cur, prev, testid }) => (
  <div className="flex items-center justify-between text-xs" data-testid={testid}>
    <span className="text-muted-foreground">{label}</span>
    <span className="flex items-center gap-1.5"><span className="font-semibold">{num(cur)}</span><Delta current={cur} previous={prev} /></span>
  </div>
);

function NewCustomersKpi({ cur, prev }) {
  const total = (d) => (d ? (d.new_online_customers || 0) + (d.new_store_customers || 0) : undefined);
  return (
    <Card className="h-full p-5 transition-colors hover:border-primary/40" data-testid="kpi-Nuovi clienti">
      <div className="flex items-start justify-between">
        <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Nuovi clienti</p>
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-violet-500/15 text-violet-400"><UserPlus className="h-[18px] w-[18px]" /></div>
      </div>
      <div className="-mt-3 flex items-baseline gap-2">
        <p className="font-display text-2xl font-bold tracking-tight" data-testid="kpi-new-customers-total">{num(total(cur))}</p>
        <Delta current={total(cur)} previous={total(prev)} />
      </div>
      <div className="mt-2 space-y-1">
        <ChannelLine label="Online" cur={cur.new_online_customers} prev={prev?.new_online_customers} testid="kpi-new-online" />
        <ChannelLine label="Negozio" cur={cur.new_store_customers} prev={prev?.new_store_customers} testid="kpi-new-store" />
      </div>
    </Card>
  );
}

function buildDaily(range, cur, prev) {
  const curMap = Object.fromEntries(cur.map((d) => [d.day, d.value]));
  const from = new Date(range.from);
  const prevFrom = subMonths(from, 1);
  const today = new Date();
  const end = from <= today && new Date(range.to) > today ? today : new Date(range.to);
  const n = differenceInCalendarDays(end, from) + 1;
  return Array.from({ length: n }, (_, i) => {
    const d = addDays(from, i);
    const key = fmt(d, "yyyy-MM-dd");
    const p = prev[i];
    return { label: dayLabel(d), value: curMap[key] ?? 0, prev: p ? p.value : null, prevLabel: p ? dayLabel(addDays(prevFrom, i)) : null };
  });
}

export default function Dashboard() {
  const [range, setRange] = useState(currentMonthRange);
  const [mode, setMode] = useReturnsMode();
  const [expanded, setExpanded] = useState({});
  const toggle = (i) => setExpanded((s) => ({ ...s, [i]: !s[i] }));
  const { data, isLoading } = useQuery({
    queryKey: ["overview", range, mode],
    queryFn: async () => (await api.get("/dashboard/overview", { params: { ...dateParams(range), returns_mode: mode } })).data,
  });

  const filtered = !!(range && range.from && range.to);
  const prev = previousRange(range);
  const { data: prevData } = useQuery({
    queryKey: ["overview", "prev", range, mode],
    queryFn: async () => (await api.get("/dashboard/overview", { params: { ...dateParams(prev), returns_mode: mode } })).data,
    enabled: !!prev,
  });
  const isShort = !!(range?.from && range?.to) && differenceInCalendarDays(new Date(range.to), new Date(range.from)) < 31;
  const prevMonthRange = isShort ? prev : null;
  const authUser = useAuth()?.user;
  const isOwner = seesMoney(authUser);
  const showCoverage = ["materie_prime", "produzione", "magazzino"].some((p) => canSee(authUser, p));
  const { data: prevMonth } = useQuery({
    queryKey: ["analytics", "prev-month", range, mode],
    queryFn: async () => (await api.get("/sales/analytics", { params: { ...dateParams(prevMonthRange), returns_mode: mode } })).data,
    enabled: isShort && isOwner,
  });

  if (isLoading || !data)
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );

  const spanDays = filtered ? differenceInCalendarDays(new Date(range.to), new Date(range.from)) + 1 : Infinity;
  const daily = spanDays <= 31;
  const chartData = daily
    ? buildDaily(range, data.revenue_by_day || [], prevMonth?.revenue_by_day || [])
    : data.revenue_by_month.map((d) => ({ ...d, label: monthLabel(d.month) }));

  return (
    <div>
      <PageHeader title="Panoramica" subtitle="I numeri chiave della tua azienda in tempo reale.">
        <ReturnsModeSelect value={mode} onChange={setMode} testid="dashboard-returns-mode" />
        <DateRangeFilter range={range} onChange={setRange} testid="dashboard-date-filter" />
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
        {isOwner && <Kpi to={canSee(authUser, "vendite") ? "/vendite" : null} icon={Euro} label={`${filtered ? "Fatturato periodo" : "Fatturato mese"}${data.revenue_source === "shopify" ? " · Shopify" : ""}`} value={eur(data.month_revenue)} sub={filtered ? `${num(data.period_orders)} ordini · media ${eur(data.period_avg_order)}` : `Totale: ${eur(data.total_revenue)}`} accent="bg-primary/15 text-primary" cur={data.month_revenue} prev={prevData?.month_revenue} />}
        <Kpi to={canSee(authUser, "ordini") ? "/ordini" : null} icon={ShoppingBag} label="Ordini in lavorazione" value={num(data.orders_in_lavorazione)} sub={`${num(data.orders_evasi)} spediti`} accent="bg-blue-500/15 text-blue-400" />
        <KpiLink to={canSee(authUser, "clienti") ? "/clienti" : null}><NewCustomersKpi cur={data} prev={prevData} /></KpiLink>
        <Kpi to={canSee(authUser, "resi") ? "/resi" : null} icon={Undo2} label="Tasso di reso" value={`${num(data.return_rate)}%`} sub={isOwner ? `${num(data.refunded_units)} pz · ${eur(data.refunded_amount)}` : `${num(data.refunded_units)} pz`} accent="bg-rose-500/15 text-rose-400" />
        <Kpi to={canSee(authUser, "magazzino") ? "/magazzino" : null} icon={Package} label="Pezzi a magazzino" value={num(data.stock_pieces)} sub={isOwner ? `Valore: ${eur(data.stock_value)}` : undefined} accent="bg-emerald-500/15 text-emerald-400" />
        <Kpi to={canSee(authUser, "produzione") ? "/produzione" : null} icon={Factory} label="Lotti in produzione" value={num(data.active_batches)} sub="Attivi ora" accent="bg-amber-500/15 text-amber-400" />
      </div>

      <MyNotes />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {isOwner && <Card className="p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">Andamento fatturato <span className="text-sm font-normal text-muted-foreground" data-testid="revenue-chart-mode">{daily ? "giorno per giorno" : "mese per mese"}</span></h3>
            <TrendingUp className="h-4 w-4 text-primary" />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} interval={daily ? "preserveStartEnd" : 0} minTickGap={8} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v / 1000}k`} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 13 }}
                formatter={(v, name) => [eur(v), name === "prev" ? "Mese precedente" : "Fatturato"]}
                labelFormatter={(l, p) => (daily ? `Giorno ${l}${p?.[0]?.payload?.prevLabel ? ` (vs ${p[0].payload.prevLabel})` : ""}` : l)}
              />
              <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#rev)" connectNulls={false} />
              {daily && <Line type="monotone" dataKey="prev" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="5 4" dot={false} />}
              {daily && <Legend verticalAlign="top" height={28} iconType="plainline" formatter={(v) => (v === "prev" ? "Mese precedente" : "Periodo attuale")} wrapperStyle={{ fontSize: 12 }} />}
            </AreaChart>
          </ResponsiveContainer>
        </Card>}

        <Card className={`p-6 ${isOwner ? "" : "lg:col-span-3"}`}>
          <h3 className="mb-4 font-display text-lg font-semibold">Top prodotti</h3>
          <div className="space-y-2">
            {data.top_products.length === 0 && <p className="text-sm text-muted-foreground">Nessuna vendita nel periodo.</p>}
            {data.top_products.map((p, i) => {
              const multi = (p.colors?.length || 0) > 1;
              const open = !!expanded[p.name];
              return (
                <div key={p.name} className="rounded-md" data-testid={`top-product-${i}`}>
                  <div
                    className={`flex items-center gap-3 rounded-md py-1.5 ${multi ? "cursor-pointer hover:bg-accent/50" : ""}`}
                    onClick={() => multi && toggle(p.name)}
                    data-testid={multi ? `top-product-toggle-${i}` : undefined}
                  >
                    <span className="w-5 font-mono-erp text-sm text-muted-foreground">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{p.name}</p>
                      <p className="font-mono-erp text-[11px] text-muted-foreground">
                        {num(p.units)} pz{multi ? ` · ${p.colors.length} colori` : (p.colors?.[0] ? ` · ${p.colors[0].color}` : "")}
                      </p>
                    </div>
                    {isOwner && <span className="text-sm font-semibold">{eur(p.revenue)}</span>}
                    {multi ? (
                      open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <span className="w-4" />
                    )}
                  </div>
                  {open && multi && (
                    <div className="ml-8 mt-1 space-y-1.5 border-l border-border/50 pl-3 pb-1">
                      {p.colors.map((c, j) => (
                        <div key={c.color || j} className="flex items-center justify-between text-xs" data-testid={`top-product-${i}-color-${j}`}>
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <span className="inline-block h-2 w-2 rounded-full bg-primary/50" />
                            {c.color}
                          </span>
                          <span className="flex items-center gap-3">
                            <span className="text-muted-foreground">{num(c.units)} pz</span>
                            {isOwner && <span className="w-16 text-right font-medium text-foreground">{eur(c.revenue)}</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {isOwner && <Card className="p-6">
          <h3 className="mb-4 font-display text-lg font-semibold">Vendite per categoria</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.by_category} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v / 1000}k`} />
              <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} width={90} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 13 }}
                formatter={(v) => [eur(v), "Venduto"]}
                cursor={{ fill: "hsl(var(--accent))" }}
              />
              <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>}

        <Card className="p-6">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-400" />
            <h3 className="font-display text-lg font-semibold">Avvisi scorte</h3>
          </div>
          <div className="space-y-4">
            <div>
              <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Materie prime critiche</p>
              {data.low_materials.length === 0 && <p className="text-sm text-muted-foreground">Tutto a posto.</p>}
              {data.low_materials.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-1.5 text-sm">
                  <span>{m.name}</span>
                  <Badge variant="outline" className="border-rose-500/30 bg-rose-500/10 text-rose-400">
                    {num(m.quantity)} {m.unit}
                  </Badge>
                </div>
              ))}
            </div>
            <div>
              <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Capi sotto scorta</p>
              {data.low_stock_products.length === 0 && <p className="text-sm text-muted-foreground">Tutto a posto.</p>}
              {data.low_stock_products.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-1.5 text-sm">
                  <span>{p.name}</span>
                  <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-400">
                    {num(p.stock)} pz
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
      <div className="mt-6"><PaymentMethods data={data.payments} showMoney={isOwner} /></div>
      {showCoverage && <div className="mt-6"><MaterialsCoverage testid="dashboard-coverage" /></div>}
    </div>
  );
}
